import type { ActiveTimer } from '../../types';
import type { WorkEngagementOption } from './timerModel';
import type { ClosableItem } from '../home/useCloseTicket';

/**
 * The timer decisions that must outlive the button or dialog that asked
 * for them, answered by ONE always-mounted host (`TimerFlowHost`, inside
 * `GlobalTimer`):
 *   - "החתמת כניסה": starting a timer while not clocked in today asks
 *     "החתמת כניסה (משרד) והתחלה" / "החתמת כניסה (בית) והתחלה" / "ביטול"
 *     (P-10a, D-66 — "רק טיימר" is gone: the server refuses a timer without
 *     a clock-in, 409 CLOCK_IN_REQUIRED). Asked before the switch question.
 *   - D-98 "בהפסקה": starting a timer during a break asks "לסיים אותה
 *     ולהתחיל?" (asked where the clock-in question would be);
 *   - D-129 / QX-59 "חזרה לעבודה": starting a timer after clocking out today
 *     asks "יצאת היום ב-13:00. לחזור לעבודה ולהתחיל את '...'?" —
 *     [חזרה לעבודה והתחלה] [ביטול] (never returns to work silently);
 *   - "החלפת טיימר": starting a timer while another task's timer runs asks
 *     "עצירת ‹א› ומעבר" / "להריץ את שניהם" (owner 2026-10-11).
 *   - D-64: after ⏸ the task's status circles show for a moment (optional
 *     change), from the host because the button that stopped may unmount.
 *   - "וגם לסגור את המשימה" after stopping a timer: the close runs through
 *     `useCloseTicket` (its checks and its 6 s "ביטול") in the host, because
 *     the stop dialog and its owner unmount as soon as the timer stops.
 */

export type TimerSwitchChoice = 'switch' | 'both';

export interface TimerSwitchRequest {
  task: { id: string; title: string };
  /** The running timers on other tasks (one or more). */
  others: ActiveTimer[];
}

/** Where to clock in before the timer starts (D-66: there is no "timer only"). */
export type ClockInFirstChoice = 'office' | 'home';

export interface ClockInFirstRequest {
  task: { id: string; title: string };
  /** QX-1283 — yesterday's shift was closed by the 00:00 automatic clock-out: the dialog says so. */
  autoClockout?: boolean;
}

/** D-129 / QX-59 — already clocked out today: the clock-out time, for the question. */
export interface BackToWorkFirstRequest extends ClockInFirstRequest {
  clockedOutAt?: string | null;
}

/** D-146 (Q-98) — "לאיזו התקשרות לרשום את השעות?" for a ticket without one. */
export interface WorkEngagementRequest {
  task: { id: string; title: string };
  engagements: WorkEngagementOption[];
  /** FU-53: 'log' = a manual work log ("רישום על ההתקשרות"); default 'start' (a timer). */
  mode?: 'start' | 'log';
}

interface TimerFlowHostHandlers {
  ask: (req: TimerSwitchRequest, answer: (choice: TimerSwitchChoice | null) => void) => void;
  askClockIn: (req: ClockInFirstRequest, answer: (choice: ClockInFirstChoice | null) => void) => void;
  askEndBreak: (req: ClockInFirstRequest, answer: (end: boolean) => void) => void;
  askBackToWork: (req: BackToWorkFirstRequest, answer: (back: boolean) => void) => void;
  askEngagement: (req: WorkEngagementRequest, answer: (engagementId: string | null) => void) => void;
  close: (item: ClosableItem) => void;
  statusAfterStop: (req: StatusAfterStopRequest) => void;
}

/** D-64 — the task whose status circles show for a moment after ⏸. */
export interface StatusAfterStopRequest {
  ticketId: string;
  title: string;
  status: string;
  /** P-10e (D-82) — the task's saved percent (the bar under the circles). */
  progress?: number | null;
}

let host: TimerFlowHostHandlers | null = null;

/** Called by `TimerFlowHost` on mount; returns the unregister function. */
export function registerTimerFlowHost(handlers: TimerFlowHostHandlers): () => void {
  host = handlers;
  return () => { if (host === handlers) host = null; };
}

/**
 * Ask how to start next to the running timer(s). Resolves null on "ביטול".
 * Without a mounted host it keeps the server's behaviour (both run).
 */
export function askTimerSwitch(req: TimerSwitchRequest): Promise<TimerSwitchChoice | null> {
  const h = host;
  if (!h) return Promise.resolve('both');
  return new Promise((resolve) => h.ask(req, resolve));
}

/**
 * Ask where to clock in before starting (no clock-in today). Resolves null on
 * "ביטול" / close / Escape (nothing starts). Without a mounted host nothing
 * starts either: the server would refuse the timer (D-66).
 */
export function askClockInFirst(req: ClockInFirstRequest): Promise<ClockInFirstChoice | null> {
  const h = host;
  if (!h) return Promise.resolve(null);
  return new Promise((resolve) => h.askClockIn(req, resolve));
}

/**
 * D-98 — starting a timer during a break asks "את/ה בהפסקה. לסיים אותה
 * ולהתחיל?" (the same way as the clock-in question). Resolves false on
 * "ביטול" / close; without a host nothing starts (the server refuses, 409
 * ON_BREAK).
 */
export function askEndBreakFirst(req: ClockInFirstRequest): Promise<boolean> {
  const h = host;
  if (!h) return Promise.resolve(false);
  return new Promise((resolve) => h.askEndBreak(req, resolve));
}

/**
 * D-129 / QX-59 — starting a timer after today's clock-out asks first:
 * "יצאת היום ב-HH:MM. לחזור לעבודה ולהתחיל את '...'?" Resolves false on
 * "ביטול" / close; without a host nothing starts (no silent return to work).
 */
export function askBackToWorkFirst(req: BackToWorkFirstRequest): Promise<boolean> {
  const h = host;
  if (!h) return Promise.resolve(false);
  return new Promise((resolve) => h.askBackToWork(req, resolve));
}

/**
 * D-146 (Q-98) — a ticket opened without an engagement: ask on which one to
 * log the hours. Resolves the chosen id, or null on "ביטול" (nothing starts);
 * without a host nothing starts either.
 */
export function askWorkEngagement(req: WorkEngagementRequest): Promise<string | null> {
  const h = host;
  if (!h) return Promise.resolve(null);
  return new Promise((resolve) => h.askEngagement(req, resolve));
}

/** D-64 — show the task's status circles for a moment after ⏸ (no-op without a host). */
export function showStatusAfterStop(req: StatusAfterStopRequest): void {
  host?.statusAfterStop(req);
}

/** Close a task after its timer stopped. False when no host can run the close. */
export function requestCloseAfterTimer(item: ClosableItem): boolean {
  if (!host) return false;
  host.close(item);
  return true;
}

/** Whether the close-after-stop option can be offered at all. */
export function canCloseAfterTimer(): boolean {
  return host !== null;
}
